import axios from "axios";
import React, { useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import CustomInput from "src/app/components/CustomInput/CustomInput";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import Modal, { ModalTitle } from "src/app/components/Modal/Modal";
import generateUuid from "src/app/libs/generateUuid";
import { translateMessage } from "src/app/methods/translateMessage";
import { RootState } from "src/redux/reducers";
import { API_URLS } from "src/utils/API_URLS";
import { showErrorToast } from "src/utils/methods";
import { getDashboardList, setIDDates, setSelectedDashboard } from "src/redux";
import { showToast } from "src/app/methods/showToast";
import { getRoute, ROUTES } from "src/utils/ROUTES";
import {
  SETUP_MODE_QUERY_PARAM_KEY,
  SetupModes,
} from "src/app/components/InsightsDashboard/panes/InsightsDashboardSetupPane/InsightsDashboardSetupPane";
import {
  mapInsightsDashboardDateRangeToRequest,
  mapInsightsDashboardDatesToRequest,
  mapInsightsDashboardSetupToRequest,
} from "src/app/components/InsightsDashboard/utils";
import { useHistory, useLocation } from "react-router";

import "./SaveDashboardModal.scss";

interface Props {
  onClose: () => void;
  isDuplicate?: boolean;
  openSetupAfterCreate?: boolean;
}

export const SaveDashboardModal = (props: Props) => {
  const { onClose, isDuplicate, openSetupAfterCreate } = props;

  const { activeWorkspaceUuid } = useSelector(
    (state: RootState) => state.mainReducer,
  );
  const { setup, selectedDashboard, dateRange, dateFrom, dateTo } = useSelector(
    (state: RootState) => state.insightsDashboardReducer,
  );

  const [name, setName] = useState(
    isDuplicate ? `Copy of ${selectedDashboard?.name}` : "",
  );
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const intl = useIntl();
  const history = useHistory();
  const { search } = useLocation();
  const isNameTooLong = name.length > 128;

  const submit = async () => {
    try {
      setLoading(true);

      const newDashboardUuid = generateUuid();

      // The pane's Apply Filters / Create dashboard click already dispatches
      // the user's chosen (or duplicated) date range into redux before this
      // modal opens — just use it, for both create and duplicate.
      const newDashboardDateRange = dateRange;
      const newDashboardDateFrom = dateFrom;
      const newDashboardDateTo = dateTo;

      const newDashboard = {
        uuid: newDashboardUuid,
        name,
        filters: {
          ...mapInsightsDashboardSetupToRequest(setup),
          dateRange: mapInsightsDashboardDateRangeToRequest(
            newDashboardDateRange,
          ),
          ...mapInsightsDashboardDatesToRequest(
            newDashboardDateRange,
            newDashboardDateFrom,
            newDashboardDateTo,
          ),
        },
      };

      await axios.post(API_URLS.createInsightsDashboard, {
        ...newDashboard,
        wsWorkspaceUuid: activeWorkspaceUuid,
      });

      dispatch(setSelectedDashboard(newDashboard));
      dispatch(
        setIDDates({
          dateRange: newDashboardDateRange,
          dateFrom: newDashboardDateFrom,
          dateTo: newDashboardDateTo,
        }),
      );
      dispatch(getDashboardList(activeWorkspaceUuid));

      const searchParams = new URLSearchParams(search);
      searchParams.delete(SETUP_MODE_QUERY_PARAM_KEY);
      if (openSetupAfterCreate) {
        searchParams.set(SETUP_MODE_QUERY_PARAM_KEY, SetupModes.Edit);
      }
      const newSearch = searchParams.toString();

      history.push({
        pathname: getRoute(ROUTES.SOCIAL_INSIGHTS_DASHBOARD, {
          wsWorkspaceUuid: activeWorkspaceUuid,
          wsDashboardUuid: newDashboardUuid,
        }),
        search: newSearch,
      });
      showToast(
        "info",
        <IDHFormattedMessage id="ws_info" defaultMessage="Info" />,
        <IDHFormattedMessage
          id="ws_dashboard_has_been_saved_successfully"
          defaultMessage="Dashboard has been saved successfully"
        />,
      );
      onClose();
    } catch (err) {
      showErrorToast();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      className="save-dashboard-modal"
      overlayClassName="save-dashboard-modal__overlay"
      onClose={onClose}
      closeButtonText={
        <IDHFormattedMessage id="ws_cancel" defaultMessage="Cancel" />
      }
      displayCancelButton
      closeButtonVariant="white-with-light-grey-border"
      confirmButtonDisabled={name.trim().length < 3 || isNameTooLong}
      confirmButtonVariant="blue"
      confirmButtonText={
        <IDHFormattedMessage id="ws_save" defaultMessage="Save" />
      }
      confirmButtonLoading={loading}
      onConfirmClick={submit}
    >
      <ModalTitle>
        <span>
          <IDHFormattedMessage
            id="ws_save_settings_as_a_new_dashboard"
            defaultMessage="Save dashboard as"
          />
          ...
        </span>
      </ModalTitle>
      <CustomInput
        value={name}
        onChange={(e) => setName(e.target.value)}
        error={isNameTooLong}
        placeholder={translateMessage({
          intl,
          id: "ws_enter_name",
          defaultMessage: "Enter name",
        })}
      />
      {isNameTooLong && (
        <div className="save-dashboard-modal__validation-message">
          <IDHFormattedMessage
            id="ws_dashboard_name_max_128_characters"
            defaultMessage="Dashboard name cannot exceed 128 characters"
          />
        </div>
      )}
    </Modal>
  );
};
